import React, { useRef, useEffect } from "react"
import ReactDOM from "react-dom"
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const { FiX } = FiIcons;

export default function Modal({ title, content, children, isOpen, onClose, size = "max-w-lg" }) {
  const wrapperRef = useRef(null);
  const hasFocusedInitially = useRef(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside, { capture: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, { capture: true });
    };
  }, [onClose]);

  useEffect(() => {
    const html = document.querySelector("html");

    if (html) {
      if (isOpen) {
        html.style.overflowY = "hidden";

        const focusableElements =
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const modal = wrapperRef.current;
        const implantedFocusableElements = modal?.querySelectorAll(focusableElements);
        const firstFocusableElement = implantedFocusableElements?.[0];
        const focusableContent = implantedFocusableElements;
        const lastFocusableElement = focusableContent?.[focusableContent.length - 1];

        const handleKeyDown = (e) => {
          if (e.key === "Escape") {
            onClose();
            return;
          }

          const isTabPressed = e.key === "Tab" || e.keyCode === 9;
          if (!isTabPressed) return;

          // Only trap focus if the active element is within the modal
          if (!modal.contains(document.activeElement)) return;

          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElement) {
              lastFocusableElement?.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastFocusableElement) {
              firstFocusableElement?.focus();
              e.preventDefault();
            }
          }
        };

        // Set initial focus only when the modal opens and no other element is focused
        if (!hasFocusedInitially.current && firstFocusableElement && !modal.contains(document.activeElement)) {
          firstFocusableElement.focus();
          hasFocusedInitially.current = true;
        }

        document.addEventListener("keydown", handleKeyDown);
        return () => {
          document.removeEventListener("keydown", handleKeyDown);
          html.style.overflowY = "visible";
          hasFocusedInitially.current = false;
        };
      } else {
        html.style.overflowY = "visible";
        hasFocusedInitially.current = false;
      }
    }
  }, [isOpen, onClose]);

  return (
      <AnimatePresence>
      {isOpen && typeof document !== "undefined"
        ? 
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed top-0 left-0 z-50 flex items-center justify-center w-screen h-screen bg-slate-300/20 dark:bg-slate-900/50 backdrop-blur-sm"
            aria-labelledby="header-modal content-modal"
            aria-modal="true"
            role="dialog"
        >
            <div
            ref={wrapperRef}
            className={`flex lg:max-h-[90vh] max-h-[80vh] w-11/12 ${size} flex-col gap-6 rounded bg-white dark:bg-slate-800 p-6 text-slate-500 dark:text-slate-300 shadow-xl shadow-slate-700/10 dark:shadow-slate-900/20`}
            id="modal"
            role="document"
            >
            <header id="header-modal" className="flex items-center gap-4">
                <h3 className="flex-1 text-xl font-medium text-slate-700 dark:text-slate-100">
                {title}
                </h3>
                <button
                onClick={() => onClose()}
                className="inline-flex items-center justify-center h-8 w-8 text-sm font-medium transition duration-200 rounded-full text-slate-500 hover:text-slate-700 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800 dark:focus-visible:ring-slate-400 dark:focus-visible:bg-slate-800 disabled:cursor-not-allowed disabled:text-slate-300 disabled:hover:bg-transparent dark:disabled:text-slate-500 dark:disabled:hover:bg-transparent"
                aria-label="Close dialog"
                >
                <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
            </header>
            <div id="content-modal" className="flex-1 overflow-auto p-1">
                {children}
            </div>
            </div>
        </motion.div>
        : null}
      </AnimatePresence>
  );
}